import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, MenuItem, CircularProgress } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { useOptimisticStore } from '../../../../../context/OptimisticStoreContext';
import { useStoreData } from '../../../../../context/StoreDataContext';
import useAddUrl from '../../../../../hooks/Magasins/useAddUrl';
import useUpdateUrl from '../../../../../hooks/Magasins/useUpdateUrl';
import { toast } from 'react-toastify';

interface UrlModalProps {
    open: boolean;
    onClose: () => void;
    entityId: string | number;
    url?: any;
}

function UrlModal({ open, onClose, entityId, url }: UrlModalProps) {
    const { t } = useLanguage();
    const { addUrl, isLoading: adding } = useAddUrl();
    const { updateUrl, isLoading: updating } = useUpdateUrl();
    const { urlTypes, loading: loadingStoreData } = useStoreData();
    const { actualStore, setActualStore } = useOptimisticStore();
    const isEdit = !!url;
    const isLoading = adding || updating;

    const [urlTypeValue, setUrlTypeValue] = useState('');
    const urlRef = useRef<HTMLInputElement | null>(null);

    const getUrlTypeLabel = (urlTypeName: string) => {
        const translationKey = `pages.store.urlTypes.${urlTypeName}`;
        const translatedLabel = t(translationKey);

        return translatedLabel === translationKey ? urlTypeName : translatedLabel;
    };

    useEffect(() => {
        if (!open) return;

        setUrlTypeValue(url?.URL_TYPE_ID?.toString() || urlTypes[0]?.id?.toString() || '');
    }, [open, url, urlTypes]);

    const handleSave = async () => {
        const urlValue = urlRef.current?.value || '';
        const selectedUrlType = urlTypeValue || urlTypes[0]?.id?.toString() || '';
        
        if (!urlValue) {
            toast.error(t('pages.store.errors.urlRequired'));
            return;
        }

        if (!selectedUrlType) {
            toast.error(t('pages.store.errors.urlTypeRequired'));
            return;
        }

        if (isEdit) {
            if (actualStore) {
                const updatedUrls = (actualStore.URL || []).map((u: any) =>
                    u.ENT_ENTITY_URL_ID === url.ENT_ENTITY_URL_ID ? { ...u, URL: urlValue, URL_TYPE_ID: selectedUrlType } : u
                );
                setActualStore({ ...actualStore, URL: updatedUrls });
            }

            const result = await updateUrl({
                ent_entity_url_id: url.ENT_ENTITY_URL_ID,
                url: urlValue,
                url_type_id: selectedUrlType,
            });

            if (result) {
                toast.success(t('pages.store.success.urlUpdated'));
                onClose();
            } else {
                if (actualStore) {
                    const updatedUrls = (actualStore.URL || []).map((u: any) => u.ENT_ENTITY_URL_ID === url.ENT_ENTITY_URL_ID ? url : u);
                    setActualStore({ ...actualStore, URL: updatedUrls });
                }
                toast.error(t('pages.store.errors.urlUpdateFailed'));
            }
        } else {
            const tempUrl = { ENT_ENTITY_URL_ID: `temp-${Date.now()}`, URL: urlValue, URL_TYPE_ID: selectedUrlType };

            if (actualStore) {
                setActualStore({ ...actualStore, URL: [...(actualStore.URL || []), tempUrl] });
            }

            const result = await addUrl({
                entity_id: entityId,
                url: urlValue,
                url_type_id: selectedUrlType,
            });

            if (result) {
                if (actualStore) {
                    // S'assurer que ENT_ENTITY_URL_ID est toujours défini
                    const urlId = result.ENT_ENTITY_URL_ID || result.id || tempUrl.ENT_ENTITY_URL_ID;
                    const newUrl = {
                        ...tempUrl,
                        ...result,
                        ENT_ENTITY_URL_ID: urlId
                    };
                    const updatedUrls = [
                        ...(actualStore.URL || []).filter((u: any) => !u.ENT_ENTITY_URL_ID.toString().startsWith('temp-')),
                        newUrl
                    ];
                    setActualStore({ ...actualStore, URL: updatedUrls });
                }
                toast.success(t('pages.store.success.urlCreated'));
                onClose();
            } else {
                if (actualStore) {
                    const updatedUrls = (actualStore.URL || []).filter((u: any) => u.ENT_ENTITY_URL_ID !== tempUrl.ENT_ENTITY_URL_ID);
                    setActualStore({ ...actualStore, URL: updatedUrls });
                }
                toast.error(t('pages.store.errors.urlCreateFailed'));
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {isEdit ? t('pages.store.editUrl') : t('pages.store.addUrl')}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '20px!important' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
                    <TextField
                        select
                        fullWidth
                        required
                        label={t('pages.store.urlType')}
                        value={urlTypeValue}
                        onChange={(event) => setUrlTypeValue(event.target.value)}
                        disabled={isLoading || loadingStoreData || urlTypes.length === 0}
                    >
                        {urlTypes.map((type) => (
                            <MenuItem key={type.id.toString()} value={type.id.toString()}>
                                {getUrlTypeLabel(type.name)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        fullWidth
                        required
                        label={t('pages.store.url')}
                        inputRef={urlRef}
                        defaultValue={url?.URL || ''}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {t('common.cancel')}
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    disabled={isLoading || loadingStoreData || urlTypes.length === 0}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? t('common.saving') : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UrlModal;
